/**
 * The SHARED, security-critical allowlist prose renderer. This is the ONE source
 * of the parse + link allowlist used BOTH by the studio's on-screen IssueRenderer
 * (which walks the AST to React elements) and by @nuff/email (which walks the same
 * AST to email-client-safe HTML + a plain-text body). Because both consumers parse
 * through {@link parseProse} and gate links through {@link isHttpUrl}, the email a
 * subscriber receives and the preview the owner sees can never disagree about what
 * is a link and what stays inert text.
 *
 * SECURITY: block bodies are model/assembly-authored and therefore untrusted. The
 * parser interprets ONLY paragraphs, unordered lists, and inline `[text](url)`
 * links; everything else (raw HTML) is carried as literal TEXT nodes. A `[text](url)`
 * becomes a link node ONLY when the url parses as http(s) via {@link isHttpUrl};
 * when it does not (a `javascript:`/`data:` scheme, or a `ref:N` placeholder that was
 * never substituted) the parenthetical is DROPPED and only the label text is kept, so
 * a reader never sees raw `[label](ref:5)` markdown or an unsafe url. Downstream
 * renderers escape every text node, so nothing unsafe reaches the DOM or an email
 * client, and no `javascript:`/`data:` url can ever become an href.
 */

/** One run of plain text inside a paragraph or list item. Always escaped downstream. */
export interface ProseTextNode {
  readonly type: 'text';
  readonly value: string;
}

/** An allowlisted inline link: `text` is the label, `url` is a proven http(s) url. */
export interface ProseLinkNode {
  readonly type: 'link';
  readonly text: string;
  readonly url: string;
}

/** One inline node: escaped text, or an allowlisted http(s) link. */
export type ProseInline = ProseTextNode | ProseLinkNode;

/** A paragraph: a sequence of inline nodes rendered joined together. */
export interface ProseParagraph {
  readonly type: 'paragraph';
  readonly children: readonly ProseInline[];
}

/** An unordered list: each item is its own sequence of inline nodes. */
export interface ProseList {
  readonly type: 'list';
  readonly items: readonly (readonly ProseInline[])[];
}

/** A top-level prose block: a paragraph or an unordered list. */
export type ProseBlock = ProseParagraph | ProseList;

/** True only for a well-formed absolute http(s) URL. Everything else is rejected. */
export function isHttpUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:';
}

// Inline `[text](url)`: a non-greedy label and a URL. The url run stops at the first
// UNBALANCED ')' but tolerates a `(...)` PAIR inside the url, so a real link whose url
// legitimately carries parens (e.g. a Wikipedia `..._(disambiguation)` article) is NOT
// truncated at that internal ')'. Deliberately still simple: whitespace ends the url,
// and only one level of nesting is tolerated (enough for real urls); anything fancier
// than a `[text](url)` shape is left as plain text.
const INLINE_LINK = /\[([^\]]+)\]\(((?:[^()\s]|\([^()]*\))*)\)/g;

/** A BARE (un-bracketed) http(s) url sitting directly in the text. */
const BARE_URL = /https?:\/\/[^\s<>()[\]]+/g;

/** The host of a url with a leading `www.` dropped, or null when unparseable. */
function hostOf(url: string): string | null {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/**
 * A Google News REDIRECT url (`news.google.com/rss/articles/CBMi...` and the like).
 * These are opaque redirect blobs, never a real article, so we NEVER render them as a
 * link and NEVER show the raw url: we drop the url and keep only any link text.
 */
function isGoogleNewsUrl(url: string): boolean {
  return hostOf(url) === 'news.google.com';
}

/**
 * Parse ONE line of prose into inline nodes, turning inline `[text](url)` into a
 * link node ONLY when the url is http(s). When the url is NOT http(s) (a failed
 * `ref:N` placeholder that was never substituted, a `javascript:`/`data:` scheme, or
 * any other non-web target) we keep ONLY the label text and DROP the parenthetical:
 * the reader never sees raw `[label](ref:5)` markdown or a dangerous url, just the
 * plain, escaped words. Adjacent text runs are merged so the AST is stable and minimal.
 */
function parseInline(text: string): ProseInline[] {
  const nodes: ProseInline[] = [];
  // Merge consecutive text runs so a rejected link fuses with its surrounding
  // text into one node (a stable, minimal AST).
  const pushText = (value: string): void => {
    if (value.length === 0) return;
    const last = nodes[nodes.length - 1];
    if (last?.type === 'text') {
      nodes[nodes.length - 1] = { type: 'text', value: last.value + value };
    } else {
      nodes.push({ type: 'text', value });
    }
  };
  // Emit a plain-text run, but turn any BARE http(s) url inside it into a clickable
  // link so a long url never renders as raw text ("links that aren't links"). A bare
  // url shows its tidy hostname as the label, not the long url. A Google News redirect
  // url is dropped entirely (kept out of the text), never linked.
  const pushRun = (run: string): void => {
    let idx = 0;
    const urls = new RegExp(BARE_URL);
    let m: RegExpExecArray | null;
    while ((m = urls.exec(run)) !== null) {
      let url = m[0];
      // A trailing sentence mark (".", ",", etc.) is punctuation, not part of the url.
      const trail = /[.,;:!?]+$/.exec(url);
      const trailing = trail?.[0] ?? '';
      if (trailing.length > 0) url = url.slice(0, -trailing.length);
      if (m.index > idx) pushText(run.slice(idx, m.index));
      idx = m.index + m[0].length;
      if (isGoogleNewsUrl(url)) {
        // Drop the redirect blob entirely; leave a single space so words don't fuse.
        pushText(' ');
      } else if (isHttpUrl(url)) {
        nodes.push({ type: 'link', text: hostOf(url) ?? url, url });
      } else {
        pushText(url);
      }
      if (trailing.length > 0) pushText(trailing);
    }
    if (idx < run.length) pushText(run.slice(idx));
  };

  let lastIndex = 0;
  // Fresh matcher per call: a RegExp with the g flag is stateful across calls.
  const matcher = new RegExp(INLINE_LINK);
  let match: RegExpExecArray | null;
  while ((match = matcher.exec(text)) !== null) {
    const full = match[0] ?? '';
    const label = match[1] ?? '';
    const rawUrl = match[2] ?? '';
    if (match.index > lastIndex) {
      pushRun(text.slice(lastIndex, match.index));
    }
    const url = rawUrl.trim();
    if (isGoogleNewsUrl(url)) {
      // A Google News redirect target: drop the link, keep only its label words.
      pushText(label);
    } else if (isHttpUrl(url)) {
      nodes.push({ type: 'link', text: label, url });
    } else {
      // Not an http(s) link: keep only the label as plain text (never the raw
      // `[label](url)` source), so a stray `ref:N` or non-web url is scrubbed to words.
      pushText(label);
    }
    lastIndex = match.index + full.length;
  }
  if (lastIndex < text.length) {
    pushRun(text.slice(lastIndex));
  }
  return nodes;
}

/**
 * Parse a block's prose body into a SAFE AST. The allowlist:
 *  - blank lines separate blocks;
 *  - a run of lines starting with `- ` or `* ` becomes one unordered list;
 *  - every other run of lines becomes a paragraph (its lines joined by a space);
 *  - inline `[text](url)` becomes an http(s)-only link node.
 * Nothing else is interpreted; raw HTML stays as text nodes for downstream escaping.
 */
export function parseProse(body: string): ProseBlock[] {
  const blocks: ProseBlock[] = [];
  const lines = body.replace(/\r\n/g, '\n').split('\n');

  let paragraph: string[] = [];
  let listItems: string[] = [];

  const flushParagraph = (): void => {
    if (paragraph.length === 0) return;
    blocks.push({ type: 'paragraph', children: parseInline(paragraph.join(' ')) });
    paragraph = [];
  };

  const flushList = (): void => {
    if (listItems.length === 0) return;
    blocks.push({ type: 'list', items: listItems.map((item) => parseInline(item)) });
    listItems = [];
  };

  for (const raw of lines) {
    const line = raw.trim();
    const listMatch = /^[-*]\s+(.*)$/.exec(line);
    if (listMatch) {
      flushParagraph();
      listItems.push(listMatch[1] ?? '');
    } else if (line.length === 0) {
      flushParagraph();
      flushList();
    } else {
      flushList();
      paragraph.push(line);
    }
  }
  flushParagraph();
  flushList();
  return blocks;
}

/** Escape the five HTML-significant characters for safe use in text or attributes. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Render inline nodes to escaped HTML: text is escaped, links become safe anchors. */
function inlineToHtml(nodes: readonly ProseInline[]): string {
  return nodes
    .map((node) =>
      node.type === 'link'
        ? `<a href="${escapeHtml(node.url)}" rel="noreferrer noopener">${escapeHtml(node.text)}</a>`
        : escapeHtml(node.value),
    )
    .join('');
}

/**
 * Render a parsed prose AST to escaped, email-client-safe inline HTML: `<p>` for
 * paragraphs, `<ul><li>` for lists, and `<a href rel>` only for allowlisted
 * http(s) links. Every text node is escaped, so no raw HTML from the body survives.
 */
export function proseToHtml(blocks: readonly ProseBlock[]): string {
  return blocks
    .map((block) =>
      block.type === 'list'
        ? `<ul>${block.items.map((item) => `<li>${inlineToHtml(item)}</li>`).join('')}</ul>`
        : `<p>${inlineToHtml(block.children)}</p>`,
    )
    .join('');
}

/** Render inline nodes to plain text: a link becomes `text (url)`, text is verbatim. */
function inlineToText(nodes: readonly ProseInline[]): string {
  return nodes
    .map((node) => (node.type === 'link' ? `${node.text} (${node.url})` : node.value))
    .join('');
}

/**
 * Render a parsed prose AST to a plain-text body (the multipart text/plain part):
 * paragraphs separated by a blank line, list items prefixed with `- `, and links
 * flattened to `text (url)`.
 */
export function proseToText(blocks: readonly ProseBlock[]): string {
  return blocks
    .map((block) =>
      block.type === 'list'
        ? block.items.map((item) => `- ${inlineToText(item)}`).join('\n')
        : inlineToText(block.children),
    )
    .join('\n\n');
}
